import { useState } from "react";
import { fmtS, fmtN } from "@/lib/format";
import Cell from "@/components/quote/Cell";
import { ChevronDown, DollarSign, Info } from "lucide-react";

const CORE = [
  ["Tipo de cambio", "tc", 0.01],
  ["Personal", "personal", 1],
  ["Sueldo mínimo (S/)", "sueldo_minimo", 10],
  ["Meses del proyecto", "meses", 1],
];

const ADVANCED = {
  "Viáticos": [
    ["Desayuno", "desayuno"], ["Almuerzo", "almuerzo"], ["Cena", "cena"], ["Días", "dias"],
    ["Alquiler depa", "alquiler_depa"], ["Cant. depas", "cant_depas"], ["Bidón/día", "bidon_dia"],
    ["Primer bidón", "primer_bidon"], ["Recarga", "recarga"], ["Pasaje/tramo", "pasaje_tramo"],
  ],
  "EPPS (precios unitarios)": [
    ["Casco", "casco"], ["Chaleco", "chaleco"], ["Pantalón", "pantalon"], ["Mascarilla", "mascarilla"],
    ["Guantes", "guantes"], ["Botas", "botas"], ["Polo", "polo"], ["Barbiquejo", "barbiquejo"],
    ["Auditivo", "auditivo"], ["Lentes", "lentes"],
  ],
  "Materiales": [
    ["Tornillos", "tornillos"], ["Chileta", "chileta"], ["Tarugos", "tarugos"],
    ["Adhesivo", "adhesivo"], ["Sika boom (base)", "sikaboom_base"],
  ],
  "Fianzas y otros gastos": [
    ["Contrato total", "contrato_total"], ["Adelanto (tasa)", "adelanto_rate"],
    ["F. adelanto (tasa)", "f_adelanto_rate"], ["Travesía", "travesia"],
    ["F. cumplim. (tasa)", "f_cumplim_rate"], ["Fac. venta (tasa)", "fac_venta_rate"],
    ["Tiempo cumpl.", "tiempo_cumpl"], ["F. retención (tasa)", "fnza_retencion_rate"],
    ["Tiempo retención", "tiempo_retencion"], ["SCTR", "sctr"], ["EMO", "emo"],
    ["Oficina", "oficina"], ["Otros gastos", "otros_gastos"],
  ],
};

export default function DolaresTab({ dollarParams, lines, totals, onDollar, onLine, onLineAll, references, isAdmin = true }) {
  const [open, setOpen] = useState(null);
  const [bulkUtil, setBulkUtil] = useState("");
  const [bulkInst, setBulkInst] = useState("");
  const [bulkMaq, setBulkMaq] = useState("");
  const t = totals || {};
  const cerraduras = references?.cerraduras || [];

  const applyBulkUtil = () => {
    const v = parseFloat(bulkUtil);
    if (!isNaN(v) && onLineAll) onLineAll("utilidad", v);
  };
  const applyBulkInst = () => {
    const v = parseFloat(bulkInst);
    if (!isNaN(v) && onLineAll) onLineAll("instalacion_base", v);
  };
  const applyBulkMaq = () => {
    const v = parseFloat(bulkMaq);
    if (!isNaN(v) && onLineAll) onLineAll("maquillaje", v);
  };

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2">
        <DollarSign className="h-4 w-4" />
        {isAdmin
          ? "Cálculo de costos y utilidad · visible solo para administradores."
          : "Ingresa los costos manuales por puerta (instalación, maquillaje, otros, cerradura)."}
      </div>

      {/* Core params — solo admin */}
      {isAdmin && (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-display font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide">Parámetros generales</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CORE.map(([label, key, step]) => (
            <label key={key} className="block">
              <span className="text-[11px] text-slate-500">{label}</span>
              <div className="mt-1 border border-slate-200 rounded-md">
                <Cell testid={`dollar-${key}`} step={step} value={dollarParams[key] ?? ""} onChange={(v) => onDollar(key, v)} />
              </div>
            </label>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          {Object.entries(ADVANCED).map(([group, fields]) => (
            <div key={group} className="border border-slate-200 rounded-md">
              <button data-testid={`adv-toggle-${group}`} onClick={() => setOpen(open === group ? null : group)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {group}
                <ChevronDown className={`h-4 w-4 transition-transform ${open === group ? "rotate-180" : ""}`} />
              </button>
              {open === group && (
                <div className="px-3 pb-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  {fields.map(([label, key]) => (
                    <label key={key} className="block">
                      <span className="text-[11px] text-slate-500">{label}</span>
                      <div className="mt-1 border border-slate-200 rounded-md">
                        <Cell testid={`dollar-${key}`} step={0.01} value={dollarParams[key] ?? ""} onChange={(v) => onDollar(key, v)} />
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Totals — solo admin */}
      {isAdmin && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Venta total</p>
          <p className="font-mono font-semibold text-emerald-600">{fmtS(t.venta_soles)}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Costo total</p>
          <p className="font-mono font-semibold text-slate-900">{fmtS(t.costo_soles)}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Ganancia total</p>
          <p className="font-mono font-semibold text-emerald-600">{fmtS(t.ganancia)}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Suministro / Instalación</p>
          <p className="font-mono text-xs text-slate-700">{fmtS(t.suministro)} / {fmtS(t.instalacion)}</p>
        </div>
      </div>
      )}

      {/* Per-door lines */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wide">
            {isAdmin ? "Detalle por puerta" : "Costos manuales por puerta"}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-2 py-2 text-left w-20 sticky-col bg-slate-50">Código</th>
                <th className="px-2 py-2 text-right w-16">Cant.</th>
                <th className="px-2 py-2 text-right w-32">
                  <div className="flex flex-col items-end gap-1">
                    <span>% Utilidad</span>
                    <input data-testid="bulk-utilidad-input" type="number" step="0.01" value={bulkUtil}
                      onChange={(e) => setBulkUtil(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") applyBulkUtil(); }}
                      onBlur={applyBulkUtil} placeholder="Aplicar a todos"
                      title="Escribe un % y presiona Enter para aplicarlo a todas las puertas"
                      className="w-24 px-1.5 py-1 rounded border border-emerald-300 bg-emerald-50/60 text-right font-mono text-xs font-normal normal-case text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </th>
                <th className="px-2 py-2 text-right w-24">
                  <div className="flex flex-col items-end gap-1">
                    <span>Instalación</span>
                    <input data-testid="bulk-instalacion-input" type="number" step="1" value={bulkInst}
                      onChange={(e) => setBulkInst(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") applyBulkInst(); }}
                      onBlur={applyBulkInst} placeholder="Aplicar a todos"
                      title="Escribe un valor y presiona Enter para aplicarlo a todas las puertas"
                      className="w-24 px-1.5 py-1 rounded border border-emerald-300 bg-emerald-50/60 text-right font-mono text-xs font-normal normal-case text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </th>
                <th className="px-2 py-2 text-right w-24">
                  <div className="flex flex-col items-end gap-1">
                    <span>Maquillaje</span>
                    <input data-testid="bulk-maquillaje-input" type="number" step="1" value={bulkMaq}
                      onChange={(e) => setBulkMaq(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") applyBulkMaq(); }}
                      onBlur={applyBulkMaq} placeholder="Aplicar a todos"
                      title="Escribe un valor y presiona Enter para aplicarlo a todas las puertas"
                      className="w-24 px-1.5 py-1 rounded border border-emerald-300 bg-emerald-50/60 text-right font-mono text-xs font-normal normal-case text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </th>
                <th className="px-2 py-2 text-right w-24 relative group">
                  <span className="inline-flex items-center gap-1 justify-end cursor-help">
                    Otros <Info className="h-3 w-3 text-slate-400" />
                  </span>
                  {cerraduras.length > 0 && (
                    <div data-testid="otros-ref-tooltip"
                      className="absolute right-0 top-full mt-1 z-20 hidden group-hover:block w-52 bg-white border border-slate-200 rounded-md shadow-lg p-2 text-left normal-case tracking-normal">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Referencia · accesorios</p>
                      {cerraduras.map((c, i) => (
                        <div key={i} className="flex items-center justify-between py-0.5 text-[11px]">
                          <span className="text-slate-700">{c.label}</span>
                          <span className="font-mono font-semibold text-red-600">S/ {Number(c.value || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </th>
                <th className="px-2 py-2 text-right w-24">Cerradura</th>
                {isAdmin && <th className="px-2 py-2 text-right w-28">Costo (S/)</th>}
                {isAdmin && <th className="px-2 py-2 text-right w-28">Venta (S/)</th>}
                {isAdmin && <th className="px-2 py-2 text-right w-28">Ganancia (S/)</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.door_id} className="border-b border-slate-100 hover:bg-slate-50 bg-white" data-testid={`line-row-${l.door_id}`}>
                  <td className="px-2 py-1 font-mono text-xs text-slate-600 sticky-col bg-white">{l.code}</td>
                  <td className="px-2 py-1 text-right font-mono">{l.qty}</td>
                  <td className="px-1 py-1"><Cell step={0.01} testid={`line-utilidad-${l.door_id}`} value={l.utilidad} onChange={(v) => onLine(l.door_id, "utilidad", v)} /></td>
                  <td className="px-1 py-1"><Cell testid={`line-inst-${l.door_id}`} value={l.instalacion_base} onChange={(v) => onLine(l.door_id, "instalacion_base", v)} /></td>
                  <td className="px-1 py-1"><Cell testid={`line-maq-${l.door_id}`} value={l.maquillaje} onChange={(v) => onLine(l.door_id, "maquillaje", v)} /></td>
                  <td className="px-1 py-1"><Cell testid={`line-otros-${l.door_id}`} value={l.otros} onChange={(v) => onLine(l.door_id, "otros", v)} /></td>
                  <td className="px-1 py-1"><Cell testid={`line-cerr-${l.door_id}`} value={l.cerradura} onChange={(v) => onLine(l.door_id, "cerradura", v)} /></td>
                  {isAdmin && <td className="px-2 py-1 text-right font-mono text-slate-500">{fmtN(l.cost_soles)}</td>}
                  {isAdmin && (
                    <td className="px-2 py-1 text-right font-mono font-medium">
                      {fmtN(l.venta_soles)}
                      {((l.otros || 0) + (l.cerradura || 0)) > 0 && (
                        <div className="text-[10px] text-emerald-600 font-normal" data-testid={`venta-accesorios-${l.door_id}`}>
                          incl. {fmtN((l.otros || 0) + (l.cerradura || 0))} acces.
                        </div>
                      )}
                    </td>
                  )}
                  {isAdmin && <td className="px-2 py-1 text-right font-mono text-emerald-600">{fmtN(l.ganancia)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
